/**
 * TYT variant picker — wires the paper/size/frame radio pickers in tyt-product-hero.
 * On any radio change: find matching variant, update hidden id, price, ATC button state,
 * visual selected state, and URL.
 */
class TYTVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.product = null;
    this.form = null;
    this.priceEl = null;
    this.compareAtEl = null;
    this.atcBtn = null;
    this.atcLabelEl = null;
    this.idInput = null;
    this.currency = '';
  }

  connectedCallback() {
    const dataNode = this.querySelector('[data-tyt-product-json]');
    if (!dataNode) return;
    try {
      this.product = JSON.parse(dataNode.textContent);
    } catch (e) {
      console.warn('[tyt-variant-picker] product JSON parse failed', e);
      return;
    }

    this.colorImages = {};
    const colorMapNode = this.querySelector('[data-tyt-color-images]');
    if (colorMapNode) {
      try {
        this.colorImages = JSON.parse(colorMapNode.textContent) || {};
      } catch (e) {
        console.warn('[tyt-variant-picker] colour image map parse failed', e);
      }
    }

    this.colorOptionImages = {};
    const colorOptionMapNode = this.querySelector('[data-tyt-color-option-images]');
    if (colorOptionMapNode) {
      try {
        this.colorOptionImages = JSON.parse(colorOptionMapNode.textContent) || {};
      } catch (e) {
        console.warn('[tyt-variant-picker] colour-option image map parse failed', e);
      }
    }

    this.form = this.querySelector('form[data-tyt-product-form]');
    if (!this.form) return;

    this.idInput = this.form.querySelector('[data-variant-id]');
    this.priceEl = this.querySelector('[data-tyt-price]');
    this.compareAtEl = this.querySelector('[data-tyt-compare-at]');
    this.atcBtn = this.form.querySelector('[data-tyt-atc]');
    this.atcLabelEl = this.atcBtn ? this.atcBtn.querySelector('[data-tyt-atc-label]') : null;
    this.stockEl = this.querySelector('[data-tyt-stock]');
    this.currency = this.dataset.currency || '';

    this.form.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"][name^="options["]')) {
        this.onOptionChange();
      }
    });

    // Thumbnail-as-colour-picker. The thumbs live outside <tyt-variant-picker>
    // (in the gallery column), so we listen on document and delegate.
    // A plain 'click' is enough here and no pointerdown fallback is needed:
    // the thumbs are real <button>s, and iOS Safari's unreliability is with
    // taps that land on NON-interactive elements, not on buttons.
    document.addEventListener('click', (e) => {
      const thumb = e.target.closest('.tyt-pdp-gallery__thumb[data-tyt-color]');
      if (!thumb) return;
      e.preventDefault();
      // selectColor() is a deliberate no-op when that colour is already
      // selected. Re-sync the image anyway, or the thumb reads as dead: on
      // load the hero shows the curated margherita_featured_image, which need
      // NOT match the pre-selected colour. On the Inca Trail the page opens on
      // Blue while the hero is the grey shot, so tapping Blue — the one thumb
      // a customer is most likely to try first — would otherwise do nothing.
      if (!this.selectColor(thumb.dataset.tytColor)) this.onOptionChange();
    });
  }

  // Returns TRUE only when this actually moved the selection, so the caller can
  // tell "switched colour" from "that colour was already selected".
  selectColor(colorValue) {
    if (!colorValue || !this.form) return false;
    const colorIdx = this.product.options.findIndex((o) => o.toLowerCase() === 'color');
    if (colorIdx < 0) return false;
    const colorOptName = this.product.options[colorIdx];
    const radios = this.form.querySelectorAll(`input[name="options[${colorOptName}]"]`);
    const radio = Array.from(radios).find((r) => r.value === colorValue);
    if (!radio || radio.checked) return false;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  onOptionChange() {
    const selectedOptions = this.product.options.map((optName) => {
      const radio = this.form.querySelector(`input[name="options[${optName}]"]:checked`);
      return radio ? radio.value : null;
    });

    const variant = this.product.variants.find((v) => {
      return v.options.every((val, i) => val === selectedOptions[i]);
    });

    this.updateSelectedVisuals();

    // Image swap is colour-driven, not variant-availability-driven — pick Teal,
    // see Teal, even if the Teal/12×16/Full SKU doesn't exist.
    this.updateImage(variant);

    // Thumbnails follow the current Option (so switching from Full to Bright Angel Trail
    // swaps all 5 colour thumbnails to that trail's posters).
    const optionValue = this.resolveAxis(variant, 'option');
    if (optionValue) this.updateThumbnails(optionValue);

    // Price tracks the selected Size, even when the full Colour × Size × Option
    // combination isn't a real SKU. Customer picks 18×24 → sees the 18×24 price.
    const priceVariant = variant || this.resolveSizeFallbackVariant(selectedOptions);
    if (priceVariant) this.updatePrice(priceVariant);

    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;
    this.updateATC(variant);
    this.updateStock(variant);
    this.updateURL(variant);
  }

  // Find any variant whose Size axis matches what's currently selected, ignoring
  // Colour/Option. Used to surface size-driven price changes when the full combo
  // isn't a real SKU. Assumes price depends primarily on Size — true for posters.
  resolveSizeFallbackVariant(selectedOptions) {
    const sizeIdx = this.product.options.findIndex((o) => o.toLowerCase() === 'size');
    if (sizeIdx < 0) return null;
    const selectedSize = selectedOptions[sizeIdx];
    if (!selectedSize) return null;
    return this.product.variants.find((v) => v.options[sizeIdx] === selectedSize);
  }

  // Find a Color/Option-axis value from the matched variant (preferred) or
  // from the currently-checked radio (fallback when no variant matches).
  resolveAxis(variant, axisName) {
    const idx = this.product.options.findIndex((o) => o.toLowerCase() === axisName);
    if (idx < 0) return null;
    if (variant && variant.options) return variant.options[idx];
    const optName = this.product.options[idx];
    const radio = this.form.querySelector(`input[name="options[${optName}]"]:checked`);
    return radio ? radio.value : null;
  }

  // Image priority: per-(Colour × Option) → per-Colour → null.
  resolveImage(colorValue, optionValue) {
    if (colorValue && optionValue && this.colorOptionImages[colorValue] && this.colorOptionImages[colorValue][optionValue]) {
      return this.colorOptionImages[colorValue][optionValue];
    }
    if (colorValue && this.colorImages[colorValue]) {
      return this.colorImages[colorValue];
    }
    return null;
  }

  updateImage(variant) {
    const mainImg = document.querySelector('.tyt-pdp-gallery__poster img');
    if (!mainImg) return;

    const colorValue = this.resolveAxis(variant, 'color');
    const optionValue = this.resolveAxis(variant, 'option');

    let newSrc = this.resolveImage(colorValue, optionValue);

    // Shopify standard variant→media link as last resort (only if we have a real variant)
    if (!newSrc && variant && variant.featured_image && variant.featured_image.src) {
      newSrc = this.shopifyImageUrl(variant.featured_image.src, 1200);
    }

    if (!newSrc) return;
    if (mainImg.src === newSrc) return;
    mainImg.removeAttribute('srcset');
    mainImg.src = newSrc;
  }

  // Re-render all colour thumbnail images for the given option value.
  // Resizes width=1200 → width=200 in the URL so we don't load full-size into 200px thumbs.
  updateThumbnails(optionValue) {
    document.querySelectorAll('.tyt-pdp-gallery__thumb[data-tyt-color]').forEach((thumb) => {
      const color = thumb.dataset.tytColor;
      const img = thumb.querySelector('img');
      if (!img) return;
      const fullSrc = this.resolveImage(color, optionValue);
      if (!fullSrc) return;
      const thumbSrc = fullSrc.replace(/([?&])width=\d+/, '$1width=200');
      if (img.src !== thumbSrc) img.src = thumbSrc;
    });
  }

  shopifyImageUrl(src, width) {
    if (!src) return '';
    const fullSrc = src.startsWith('//') ? `https:${src}` : src;
    try {
      const url = new URL(fullSrc);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  updateSelectedVisuals() {
    this.form.querySelectorAll('label.tyt-swatch, label.tyt-tile, label.tyt-color-card').forEach((label) => {
      const radio = label.querySelector('input[type="radio"]');
      if (radio && radio.checked) {
        label.classList.add('is-selected');
      } else {
        label.classList.remove('is-selected');
      }
    });
    // Highlight the colour thumbnail matching the currently-selected Color radio
    const colorIdx = this.product.options.findIndex((o) => o.toLowerCase() === 'color');
    if (colorIdx >= 0) {
      const colorOptName = this.product.options[colorIdx];
      const checkedRadio = this.form.querySelector(`input[name="options[${colorOptName}]"]:checked`);
      const selectedColor = checkedRadio ? checkedRadio.value : null;
      document.querySelectorAll('.tyt-pdp-gallery__thumb[data-tyt-color]').forEach((thumb) => {
        if (thumb.dataset.tytColor === selectedColor) {
          thumb.classList.add('is-selected');
        } else {
          thumb.classList.remove('is-selected');
        }
      });
    }
    // Update "X selected" caption next to the paper picker, if present
    const paperSelectedEl = this.querySelector('[data-paper-selected]');
    if (paperSelectedEl) {
      const paperRadio = this.form.querySelector('input[name="options[Paper]"]:checked, input[name="options[paper]"]:checked');
      if (paperRadio) paperSelectedEl.textContent = paperRadio.value + ' selected';
    }
  }

  updatePrice(variant) {
    if (!this.priceEl) return;
    this.priceEl.textContent = this.formatMoney(variant.price);
    if (this.compareAtEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.compareAtEl.textContent = this.formatMoney(variant.compare_at_price);
        this.compareAtEl.style.display = '';
      } else {
        this.compareAtEl.style.display = 'none';
      }
    }
  }

  updateATC(variant) {
    if (!this.atcBtn) return;
    this.atcBtn.disabled = !variant.available;
    if (this.atcLabelEl) {
      this.atcLabelEl.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
  }

  updateStock(variant) {
    if (!this.stockEl) return;
    this.stockEl.textContent = variant.available
      ? 'In stock · ships in 5 days'
      : 'Sold out · email us to backorder';
    this.stockEl.style.color = variant.available ? 'var(--sage-deep)' : 'var(--ink-soft)';
  }

  setUnavailable() {
    if (this.atcBtn) {
      this.atcBtn.disabled = true;
      if (this.atcLabelEl) this.atcLabelEl.textContent = 'Unavailable';
    }
    if (this.stockEl) this.stockEl.textContent = 'This combination is unavailable';
  }

  updateURL(variant) {
    if (!variant || !history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  formatMoney(cents) {
    // Returns the number only (e.g. "36,95"). The currency code lives in a
    // separate span so the markup matches Liquid's `money_without_currency` output.
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2).replace('.', ',');
    }
  }
}

if (!customElements.get('tyt-variant-picker')) {
  customElements.define('tyt-variant-picker', TYTVariantPicker);
}
